import React from 'react';
import ReactDOM from 'react-dom/client';
import { CssBaseline } from '@mui/material';
import { RouterProvider } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ro } from 'date-fns/locale';
import { SnackbarProvider } from 'notistack';
import './index.css';
import { router } from './app/router';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { DynamicThemeProvider } from './contexts/DynamicThemeProvider';
import { AvatarProvider } from './contexts/AvatarContext';
import { RealtimeProvider } from './contexts/RealtimeContext';
import { initWebVitals, initAPITracking, trackPageLoad } from './utils/webVitals';
import { errorLogger } from './utils/errorLogger';

// ========================================
// 🔴 MONITORIZARE AUTOMATA ERORI GLOBALE
// ========================================

/**
 * Interceptor pentru toate erorile runtime JavaScript
 * Prinde erori de tip: ReferenceError, TypeError, etc.
 */
window.addEventListener('error', (event: ErrorEvent) => {
  errorLogger.log({
    timestamp: new Date().toISOString(),
    type: 'runtime',
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    stack: event.error?.stack,
  });

  // Nu previne comportamentul default (nu opreste propagarea erorii)
  return false;
});

/**
 * Interceptor pentru promise-uri negestionate (unhandled rejections)
 * Prinde erori de tip: async/await, fetch, API calls
 */
window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  errorLogger.log({
    timestamp: new Date().toISOString(),
    type: 'promise',
    message: event.reason?.message || String(event.reason),
    stack: event.reason?.stack,
  });

  // Nu previne comportamentul default
  return false;
});

/**
 * Mesaj de bun venit in consola cu instructiuni
 * Previne afisarea multipla la HMR (Hot Module Reloading)
 */
if (!window.__errorLoggerInit) {
  window.__errorLoggerInit = true;
  console.log(
    '%c🔍 SISTEM DE MONITORIZARE ERORI ACTIV',
    'background: #4CAF50; color: white; padding: 8px 16px; font-size: 14px; font-weight: bold; border-radius: 4px;'
  );
  console.log(
    '%cToate erorile din aplicatie sunt monitorizate automat!',
    'color: #2196F3; font-size: 12px; font-weight: bold;'
  );
  console.log('\n📊 Comenzi disponibile in consola:');
  console.log('  • window.errorLogger.printSummary()     - Afiseaza raport sumar');
  console.log('  • window.errorLogger.getErrors()        - Lista completa de erori');
  console.log('  • window.errorLogger.exportAsJSON()     - Export JSON');
  console.log('  • window.errorLogger.clear()            - Sterge toate erorile\n');
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <DynamicThemeProvider>
      <NotificationProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
          <CssBaseline />
          <AuthProvider>
            <AvatarProvider>
              <RealtimeProvider>
                <SnackbarProvider>
                  <RouterProvider router={router} />
                </SnackbarProvider>
              </RealtimeProvider>
            </AvatarProvider>
          </AuthProvider>
        </LocalizationProvider>
      </NotificationProvider>
    </DynamicThemeProvider>
  </React.StrictMode>
);

// Initialize Web Vitals tracking
initWebVitals();

// Initialize API tracking
initAPITracking();

// Track initial page load
if (document.readyState === 'complete') {
  trackPageLoad();
} else {
  window.addEventListener('load', trackPageLoad);
}
